import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'

const API_URL = process.env.REACT_APP_API_URL

const Resetpassword = () => {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [token, setToken] = useState('')
    const [email, setEmail] = useState('')
    const [passwordRulesVisible, setPasswordRulesVisible] = useState(false)
    const passwordInputRef = useRef(null)
    const rulesRef = useRef(null)

    useEffect(() => {
        // Extract token and email from URL query parameters
        const tokenParam = searchParams.get('token')
        const emailParam = searchParams.get('email')

        if (!tokenParam || !emailParam) {
            setErrorMessage('Invalid reset link. Please check your email for the correct link.')
            toast.error('Invalid reset link. Missing token or email.', { autoClose: 3000 })
        } else {
            setToken(tokenParam)
            // Decode email if it's URL encoded
            setEmail(decodeURIComponent(emailParam))
        }
    }, [searchParams])

    const validatePassword = (password) => {
        // Password rules: 8 chars, uppercase, lowercase, number, special char
        const minLength = password.length >= 8
        const hasUpperCase = /[A-Z]/.test(password)
        const hasLowerCase = /[a-z]/.test(password)
        const hasNumber = /\d/.test(password)
        const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)

        return {
            isValid: minLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar,
            errors: {
                minLength: !minLength,
                hasUpperCase: !hasUpperCase,
                hasLowerCase: !hasLowerCase,
                hasNumber: !hasNumber,
                hasSpecialChar: !hasSpecialChar
            }
        }
    }

    // Check if password is valid
    const isPasswordValid = (pass) => {
        if (!pass) return false
        const validation = validatePassword(pass)
        return validation.isValid
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setErrorMessage('')

        // Validation
        if (!newPassword.trim()) {
            setErrorMessage('Please enter a new password')
            return
        }

        if (!confirmPassword.trim()) {
            setErrorMessage('Please confirm your password')
            return
        }

        // Check password match
        if (newPassword !== confirmPassword) {
            setErrorMessage('Passwords do not match')
            return
        }

        // Validate password rules
        const passwordValidation = validatePassword(newPassword)
        if (!passwordValidation.isValid) {
            setErrorMessage('Password does not meet the requirements')
            return
        }

        if (!token || !email) {
            setErrorMessage('Invalid reset link. Please check your email.')
            return
        }

        setIsLoading(true)

        try {
            const payload = {
                action: 'reset-password',
                token: token,
                email: email,
                newPassword: newPassword
            }

            const response = await axios.post(API_URL, payload)

            // Parse the response - handle both string and object responses
            let responseData = response.data
            if (typeof responseData === 'string') {
                try {
                    responseData = JSON.parse(responseData)
                } catch (parseError) {
                    responseData = response.data
                }
            }

            // Check if API call was successful
            if (responseData.success === true) {
                toast.success(responseData.message || 'Password reset successfully. You can now log in with your new password.', { autoClose: 3000 })
                // Navigate to login page after a short delay
                setTimeout(() => {
                    navigate('/')
                }, 2000)
            } else {
                setErrorMessage(responseData.message || 'Failed to reset password')
                toast.error(responseData.message || 'Failed to reset password', { autoClose: 2000 })
            }
        } catch (error) {
            console.error('Reset password error:', error)
            let errorMsg = 'An error occurred while resetting password'
            if (error.response && error.response.data) {
                let errorData = error.response.data
                if (typeof errorData === 'string') {
                    try {
                        errorData = JSON.parse(errorData)
                    } catch (e) {
                        // Use error message as is
                    }
                }
                errorMsg = errorData.message || errorMsg
            }
            setErrorMessage(errorMsg)
            toast.error(errorMsg, { autoClose: 2000 })
        } finally {
            setIsLoading(false)
        }
    }

    const passwordValidation = newPassword ? validatePassword(newPassword) : null

    return (
        <div className="hold-transition login-page accent-custom">
            <div className="login-box login_div">
                <div className="card card-outline card-primary">
                    <div className="card-header text-center">
                        <div className="login_logo">
                            <img src='/assets/img/color icon_circle.png' border="0" alt="Logo" />
                        </div>
                    </div>
                    <div className="card-body d-flex flex-row flex_flow_rowwrap">
                        <div className="col-12 col-sm-12 col-md-12">
                            <h5 className="login-box-msg mb-2">Reset Password</h5>
                            {email && (
                                <p className="text-center text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                                    Resetting password for: <strong>{email}</strong>
                                </p>
                            )}
                            <form onSubmit={handleSubmit}>
                                <div className="input-group mb-3">
                                    <input
                                        ref={passwordInputRef}
                                        type="password"
                                        className="form-control"
                                        placeholder="New Password"
                                        value={newPassword}
                                        onChange={(e) => {
                                            setNewPassword(e.target.value)
                                            setErrorMessage('')
                                        }}
                                        onFocus={() => {
                                            setPasswordRulesVisible(true)
                                            // Smooth slide down animation for password rules
                                            const $ = window.$
                                            if ($) {
                                                setTimeout(() => {
                                                    $('.password-rules-wrapper').slideDown(300)
                                                }, 10)
                                            }
                                        }}
                                        onBlur={(e) => {
                                            // When tabbing away, check if password is valid before hiding
                                            // Use setTimeout to allow focus to move to next field first
                                            setTimeout(() => {
                                                // Check if focus moved to confirm password or outside
                                                const activeElement = document.activeElement
                                                const isConfirmPasswordFocused = activeElement?.name === 'confirmPassword'
                                                
                                                // Only hide if password is valid or focus moved to confirm password
                                                if (isPasswordValid(newPassword) || isConfirmPasswordFocused) {
                                                    const $ = window.$
                                                    if ($) {
                                                        $('.password-rules-wrapper').slideUp(300, () => {
                                                            setPasswordRulesVisible(false)
                                                        })
                                                    } else {
                                                        setPasswordRulesVisible(false)
                                                    }
                                                }
                                            }, 100)
                                        }}
                                        required
                                        disabled={isLoading || !token || !email}
                                    />
                                    <div className="input-group-append">
                                        <div className="input-group-text">
                                            <i className="fas fa-lock" />
                                        </div>
                                    </div>
                                </div>
                                {newPassword && passwordValidation && (
                                    <div
                                        ref={rulesRef}
                                        className="password-rules-wrapper mb-3"
                                        style={{ 
                                            display: passwordRulesVisible ? 'block' : 'none',
                                            fontSize: '0.85rem'
                                        }}
                                    >
                                        <strong className={passwordValidation.isValid ? 'text-success' : ''}>Password must contain:</strong>
                                        <ul className="mb-0" style={{ paddingLeft: '1.2rem', marginTop: '0.5rem' }}>
                                            <li className={passwordValidation.errors.minLength ? 'text-danger' : 'text-success'}>
                                                At least 8 characters
                                            </li>
                                            <li className={passwordValidation.errors.hasUpperCase ? 'text-danger' : 'text-success'}>
                                                One uppercase letter
                                            </li>
                                            <li className={passwordValidation.errors.hasLowerCase ? 'text-danger' : 'text-success'}>
                                                One lowercase letter
                                            </li>
                                            <li className={passwordValidation.errors.hasNumber ? 'text-danger' : 'text-success'}>
                                                One number
                                            </li>
                                            <li className={passwordValidation.errors.hasSpecialChar ? 'text-danger' : 'text-success'}>
                                                One special character
                                            </li>
                                        </ul>
                                    </div>
                                )}
                                <div className="input-group mb-3">
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        className="form-control"
                                        placeholder="Confirm New Password"
                                        value={confirmPassword}
                                        onChange={(e) => {
                                            setConfirmPassword(e.target.value)
                                            setErrorMessage('')
                                        }}
                                        onFocus={() => {
                                            // When clicking on confirm password, hide rules smoothly
                                            const $ = window.$
                                            if ($) {
                                                $('.password-rules-wrapper').slideUp(300, () => {
                                                    setPasswordRulesVisible(false)
                                                })
                                            } else {
                                                setPasswordRulesVisible(false)
                                            }
                                        }}
                                        required
                                        disabled={isLoading || !token || !email}
                                    />
                                    <div className="input-group-append">
                                        <div className="input-group-text">
                                            <i className="fas fa-lock" />
                                        </div>
                                    </div>
                                </div>
                                {confirmPassword && newPassword !== confirmPassword && (
                                    <div className="alert alert-danger mb-3" style={{ fontSize: '0.85rem', padding: '0.5rem' }}>
                                        Passwords do not match
                                    </div>
                                )}
                                {errorMessage && (
                                    <div className="alert alert-danger mb-3" role="alert">
                                        {errorMessage}
                                    </div>
                                )}
                                <div className="row">
                                    <div className="col-12 mt-3">
                                        <button
                                            type="submit"
                                            className="btn btn-blue btn-block"
                                            disabled={isLoading || !token || !email || !newPassword || !confirmPassword}
                                        >
                                            {isLoading ? (
                                                <PulseLoader color="#ffffff" size={8} />
                                            ) : (
                                                'Reset Password'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>
                            <p className="mb-0 text-center mt-3">
                                Go to <Link to="/" className="text-center">Login</Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Resetpassword    